/**
 * Cryptographie E2E pour la messagerie privée.
 * Utilise TweetNaCl box (Curve25519 / XSalsa20 / Poly1305) — ChaCha20 / Box.
 *
 * Modèle :
 *  - Chaque membre génère localement une paire (publicKey, secretKey).
 *  - secretKey reste sur l'appareil (localStorage).
 *  - publicKey est publiée au serveur (lue par tous les contacts pour chiffrer).
 *  - Pour envoyer un message à Bob :
 *      ciphertext = nacl.box(plaintext, nonce, bobPublicKey, mySecretKey)
 *  - Le serveur ne reçoit JAMAIS le plaintext ni la secretKey.
 *  - Au reload : on régénère la session si la clé locale a disparu, mais alors
 *    les anciens messages ne sont plus déchiffrables (limitation acceptée pour
 *    un MVP simple — pas de keyserver / pas de PIN local).
 */
import nacl from "tweetnacl";
import { decodeUTF8, encodeUTF8, encodeBase64, decodeBase64 } from "tweetnacl-util";

const KEYPAIR_STORAGE = "fui-e2e-keypair-v1";

export function getOrCreateKeyPair() {
  const stored = localStorage.getItem(KEYPAIR_STORAGE);
  if (stored) {
    try {
      const obj = JSON.parse(stored);
      return {
        publicKey: decodeBase64(obj.publicKey),
        secretKey: decodeBase64(obj.secretKey),
        publicKeyB64: obj.publicKey,
      };
    } catch (e) {
      // fall through to regenerate
    }
  }
  const kp = nacl.box.keyPair();
  const publicKey = encodeBase64(kp.publicKey);
  const secretKey = encodeBase64(kp.secretKey);
  localStorage.setItem(KEYPAIR_STORAGE, JSON.stringify({ publicKey, secretKey }));
  return { publicKey: kp.publicKey, secretKey: kp.secretKey, publicKeyB64: publicKey };
}

export function encryptForRecipient(plaintext, recipientPublicKeyB64, mySecretKey) {
  const recipientPublicKey = decodeBase64(recipientPublicKeyB64);
  const nonce = nacl.randomBytes(nacl.box.nonceLength);
  const ciphertext = nacl.box(decodeUTF8(plaintext), nonce, recipientPublicKey, mySecretKey);
  return {
    ciphertext: encodeBase64(ciphertext),
    nonce: encodeBase64(nonce),
  };
}

export function decryptFromSender(ciphertextB64, nonceB64, senderPublicKeyB64, mySecretKey) {
  try {
    const ciphertext = decodeBase64(ciphertextB64);
    const nonce = decodeBase64(nonceB64);
    const senderPublicKey = decodeBase64(senderPublicKeyB64);
    const plaintext = nacl.box.open(ciphertext, nonce, senderPublicKey, mySecretKey);
    if (!plaintext) return null;
    return encodeUTF8(plaintext);
  } catch (e) {
    return null;
  }
}

export function shortFingerprint(publicKeyB64) {
  if (!publicKeyB64) return "—";
  return publicKeyB64.slice(0, 6) + "…" + publicKeyB64.slice(-6);
}
